import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X } from "lucide-react";
import {
  PAYMENT_BUCKETS,
  WEIGHT_BUCKETS,
  LENGTH_BUCKETS,
  SLEEPS_BUCKETS,
  PRICE_BUCKETS,
  BED_TYPE_OPTIONS,
  FEATURE_OPTIONS,
  type RangeBucket,
} from "@/lib/inventoryFilterDefs";
import { useInventoryFacets } from "@/hooks/useInventoryFacets";
import { isRvTypeVariant } from "@/data/masterRvTypes";
import { useState } from "react";

export interface SidebarSelections {
  types: string[];
  years: string[]; // strings in URL form; numeric on apply
  paymentBuckets: string[];
  manufacturers: string[];
  makes: string[];
  features: string[];
  weightBuckets: string[];
  lengthBuckets: string[];
  sleepsBuckets: string[];
  priceBuckets: string[];
  bedTypes: string[];
}

interface Props {
  values: SidebarSelections;
  onChange: (key: keyof SidebarSelections, next: string[]) => void;
  onClear: () => void;
}

function toggle(arr: string[], val: string): string[] {
  return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
}

function CheckList({
  options,
  selected,
  onToggle,
  idPrefix,
}: {
  options: { id: string; label: string }[];
  selected: string[];
  onToggle: (id: string) => void;
  idPrefix: string;
}) {
  return (
    <ScrollArea className="max-h-56 pr-2">
      <div className="space-y-2">
        {options.length === 0 && (
          <p className="text-xs text-muted-foreground">No options</p>
        )}
        {options.map((opt) => {
          const id = `${idPrefix}-${opt.id}`;
          const checked = selected.includes(opt.id);
          return (
            <label key={id} htmlFor={id} className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox id={id} checked={checked} onCheckedChange={() => onToggle(opt.id)} />
              <span className="text-foreground">{opt.label}</span>
            </label>
          );
        })}
      </div>
    </ScrollArea>
  );
}

const bucketsToOpts = (b: RangeBucket[]) => b.map((x) => ({ id: x.id, label: x.label }));
const strsToOpts = (arr: string[]) => arr.map((s) => ({ id: s, label: s }));

export default function InventoryFilterSidebar({ values, onChange, onClear }: Props) {
  const { data: facets } = useInventoryFacets();
  const [openSections, setOpenSections] = useState<string[]>([]);

  const sections: Array<{
    key: keyof SidebarSelections;
    title: string;
    options: { id: string; label: string }[];
  }> = [
    {
      key: "types",
      title: "Type",
      // Collapse fuel/chassis variants ("… - Diesel") into their base type so
      // checking "Motor Home Class A" returns gas AND diesel units.
      options: strsToOpts(
        Array.from(
          new Set(
            (facets?.rvTypes ?? []).map((t) =>
              isRvTypeVariant(t) ? (t.split(" - ")[0] ?? t).trim() : t,
            ),
          ),
        ),
      ),
    },
    {
      key: "years",
      title: "Year",
      options: (facets?.years ?? []).map((y) => ({ id: String(y), label: String(y) })),
    },
    { key: "paymentBuckets", title: "Monthly Payment", options: bucketsToOpts(PAYMENT_BUCKETS) },
    { key: "manufacturers", title: "Manufacturer", options: strsToOpts(facets?.manufacturers ?? []) },
    { key: "makes", title: "Brands", options: strsToOpts(facets?.makes ?? []) },
    { key: "features", title: "Features", options: strsToOpts(FEATURE_OPTIONS) },
    { key: "weightBuckets", title: "Weight", options: bucketsToOpts(WEIGHT_BUCKETS) },
    { key: "lengthBuckets", title: "Length", options: bucketsToOpts(LENGTH_BUCKETS) },
    { key: "sleepsBuckets", title: "Sleeps", options: bucketsToOpts(SLEEPS_BUCKETS) },
    { key: "priceBuckets", title: "Price Range", options: bucketsToOpts(PRICE_BUCKETS) },
    { key: "bedTypes", title: "Bed Type", options: strsToOpts(BED_TYPE_OPTIONS) },
  ];

  const totalSelected = sections.reduce((n, s) => n + values[s.key].length, 0);

  return (
    <aside className="rounded-xl border bg-card p-4 shadow-xs">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-sm font-bold uppercase text-foreground">
          Filters {totalSelected > 0 && <span className="text-primary">({totalSelected})</span>}
        </h3>
        {totalSelected > 0 && (
          <Button variant="ghost" size="sm" onClick={onClear} className="h-7 text-xs gap-1">
            <X className="h-3 w-3" /> Clear
          </Button>
        )}
      </div>
      <Accordion type="multiple" value={openSections} onValueChange={setOpenSections} className="w-full">
        {sections.map((section) => {
          const sel = values[section.key];
          return (
            <AccordionItem key={section.key} value={section.key}>
              <AccordionTrigger className="py-3 text-sm">
                <span className="flex items-center gap-2">
                  {section.title}
                  {sel.length > 0 && (
                    <span className="rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                      {sel.length}
                    </span>
                  )}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                {openSections.includes(section.key) && <CheckList
                  options={section.options}
                  selected={sel}
                  onToggle={(id) => onChange(section.key, toggle(sel, id))}
                  idPrefix={section.key}
                />}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </aside>
  );
}